import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../components/Topbar';
import Modal from '../components/Modal';
import api, { API } from '../utils/api';
import { MagnifyingGlass, PencilSimple, Trash, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import useContentHeight from '../hooks/useContentHeight';

const Servicios = () => {
  const contentHeight = useContentHeight(280);
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [dominios, setDominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: 'Soporte',
    dominio_id: '',
    precio_base: 0,
    precio_iva: 0,
    periodicidad: 'mensual',
    fecha_registro: '',
    estado: '',
    descripcion: ''
  });

  const [includeIva, setIncludeIva] = useState(false);

  // Función para calcular precio + IVA (16%)
  const calcularPrecioIva = (precio) => {
    return parseFloat((precio * 1.16).toFixed(2));
  };

  // Categorías de servicios
  const serviciosCategories = ['Soporte', 'Seguridad', 'Mantenimiento', 'Desarrollo', 'Marketing'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProductos();
  }, [productos, searchTerm, filter]);

  const fetchData = async () => {
    try {
      const [serviciosRes, dominiosRes] = await Promise.all([
        api.get('/servicios'),
        api.get('/dominios')
      ]);
      setProductos(serviciosRes.data);
      setDominios(dominiosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const filterProductos = () => {
    let filtered = [...productos];

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.categoria.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filter === 'productos') {
      filtered = filtered.filter(p => p.tipo_producto);
    } else if (filter === 'servicios') {
      filtered = filtered.filter(p => p.tipo_servicio);
    }

    setFilteredProductos(filtered);
  };

  // Infinite scroll for mobile
  const { 
    displayedItems: displayedServicios, 
    hasMore, 
    isLoadingMore, 
    loadMoreRef,
    totalItems,
    displayedCount 
  } = useInfiniteScroll(filteredProductos, 8);

  // Pull to refresh for mobile
  const handleRefresh = useCallback(async () => {
    await fetchData();
    toast.success('Datos actualizados');
  }, []);

  const {
    containerRef,
    pullDistance,
    pullProgress,
    isRefreshing
  } = usePullToRefresh(handleRefresh);

  const handleAdd = () => {
    setEditingProducto(null);
    setIncludeIva(false);
    setFormData({
      nombre: '',
      categoria: 'Soporte',
      dominio_id: '',
      precio_base: 0,
      precio_iva: 0,
      periodicidad: 'mensual',
      fecha_registro: '',
      estado: '',
      descripcion: ''
    });
    setShowModal(true);
  };

  const handleEdit = (producto) => {
    setEditingProducto(producto);
    setIncludeIva(producto.precio_iva > 0);
    setFormData({
      nombre: producto.nombre || '',
      categoria: producto.categoria || 'Soporte',
      dominio_id: producto.dominio_id || '',
      precio_base: producto.precio_base || 0,
      precio_iva: producto.precio_iva || 0,
      periodicidad: producto.periodicidad || 'mensual',
      fecha_registro: producto.fecha_registro || '',
      estado: producto.estado || '',
      descripcion: producto.descripcion || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (productoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este servicio?')) return;

    try {
      await api.delete(`/servicios/${productoId}`);
      toast.success('Servicio eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar servicio');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const dataToSend = {
        ...formData,
        precio_base: parseFloat(formData.precio_base) || 0,
        precio_iva: includeIva ? calcularPrecioIva(parseFloat(formData.precio_base) || 0) : 0
      };
      
      if (editingProducto) {
        await api.put(`/servicios/${editingProducto.id}`, dataToSend);
        toast.success('Servicio actualizado exitosamente');
      } else {
        await api.post('/servicios', dataToSend);
        toast.success('Servicio creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Error al guardar servicio');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/export/productos?token=${token}`, '_blank');
      toast.success('Exportando productos...');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      'Hosting': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]',
      'Desarrollo': 'bg-[#b06eff]/10 border-[#b06eff] text-[#b06eff]',
      'Mantenimiento': 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]',
      'Dominio': 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]',
      'Soporte': 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]',
      'Seguridad': 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]',
      'Email': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]',
      'Backup': 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]',
      'Performance': 'bg-[#b06eff]/10 border-[#b06eff] text-[#b06eff]',
      'Marketing': 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]'
    };
    return colors[categoria] || 'bg-[#8b949e]/10 border-[#8b949e] text-[#8b949e]';
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      'activo': 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]',
      'por vencer': 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]',
      'vencido': 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]',
      'pagado': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]'
    };
    return styles[estado] || styles['activo'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0d0f14]">
        <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen" ref={containerRef}>
      <Topbar
        title="Servicios"
        breadcrumb="Visionweb / Módulos / Servicios"
        onAdd={handleAdd}
        onExport={handleExport}
        showAdd={true}
        showExport={true}
      />

      <div className="p-4 md:p-6 pb-12">
        {/* Pull to Refresh Indicator - Mobile only */}
        <div className="block md:hidden">
          <PullToRefreshIndicator 
            pullDistance={pullDistance}
            pullProgress={pullProgress}
            isRefreshing={isRefreshing}
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar servicios..."
              className="w-full bg-[#13161e] border border-[#2a2e3d] rounded-sm pl-10 pr-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {['todos', 'productos', 'servicios'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 md:px-4 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap transition-colors ${
                  filter === f
                    ? 'bg-[#4f9eff] text-[#0d0f14] font-bold'
                    : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white'
                }`}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Cards View with Infinite Scroll */}
        <div className="block md:hidden overflow-y-auto space-y-3 pr-1" style={{ maxHeight: contentHeight }}>
          {displayedServicios.map((producto) => {
            const dominio = dominios.find(d => d.id === producto.dominio_id);
            return (
              <div key={producto.id} className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4" data-testid={`servicio-card-${producto.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-['Mulish'] text-sm font-semibold truncate">{producto.nombre}</div>
                    <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs truncate">{dominio?.nombre || '-'}</div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] uppercase ml-2 ${getEstadoBadge(producto.estado)}`}>
                    {producto.estado || 'activo'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-['IBM_Plex_Mono'] font-bold">${producto.precio_base?.toFixed(2) || '0.00'}</span>
                    {producto.precio_iva > 0 && (
                      <span className="text-[#00d68f] font-['IBM_Plex_Mono']">+IVA: ${producto.precio_iva.toFixed(2)}</span>
                    )}
                  </div>
                  <span className="text-[#8b949e] font-['IBM_Plex_Mono']">{producto.fecha_vencimiento || producto.fecha_registro || '-'}</span>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#2a2e3d]">
                  <button
                    onClick={() => handleEdit(producto)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#4f9eff]/10 border border-[#4f9eff] text-[#4f9eff] rounded-sm text-xs font-['IBM_Plex_Mono']"
                    data-testid={`edit-servicio-${producto.id}`}
                  >
                    <PencilSimple size={14} /> Editar
                  </button>
                  <button
                    onClick={() => handleDelete(producto.id)}
                    className="px-3 py-2 bg-[#ff5252]/10 border border-[#ff5252] text-[#ff5252] rounded-sm"
                    data-testid={`delete-servicio-${producto.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          
          {/* Load more trigger & indicator */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-4">
              {isLoadingMore ? (
                <div className="flex items-center gap-2 text-[#4f9eff]">
                  <SpinnerGap size={20} className="animate-spin" />
                  <span className="font-['IBM_Plex_Mono'] text-xs">Cargando más...</span>
                </div>
              ) : (
                <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">
                  Mostrando {displayedCount} de {totalItems}
                </div>
              )}
            </div>
          )}
          
          {!hasMore && displayedServicios.length > 0 && (
            <div className="text-center py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">
              {totalItems} servicios en total
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-[#13161e] border border-[#2a2e3d] rounded-md overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <table className="w-full">
              <thead className="bg-[#0d0f14] border-b border-[#2a2e3d] sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Servicio</th>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Dominio</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Precio</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">+ IVA</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e3d]">
                {filteredProductos.map((producto) => {
                  const dominio = dominios.find(d => d.id === producto.dominio_id);
                  return (
                    <tr key={producto.id} className="hover:bg-[#1a1e28] transition-colors group" data-testid={`producto-row-${producto.id}`}>
                      <td className="px-6 py-4 text-white font-['Mulish'] text-sm">{producto.nombre}</td>
                      <td className="px-6 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm">
                        {dominio?.nombre || '-'}
                      </td>
                      <td className="px-4 py-4 text-white font-['IBM_Plex_Mono'] text-sm font-bold">
                        ${producto.precio_base?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-4 text-[#00d68f] font-['IBM_Plex_Mono'] text-sm">
                        {producto.precio_iva > 0 ? `$${producto.precio_iva.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-4 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">
                        {producto.fecha_vencimiento || producto.fecha_registro || '-'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(producto)}
                            className="text-[#4f9eff] hover:text-white transition-colors"
                            data-testid={`edit-producto-${producto.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(producto.id)}
                            className="text-[#ff5252] hover:text-white transition-colors"
                            data-testid={`delete-producto-${producto.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {filteredProductos.length === 0 && (
          <div className="text-center py-12">
            <div className="text-[#8b949e] font-['Mulish'] text-sm">No se encontraron servicios</div>
          </div>
        )}
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingProducto ? 'Editar Servicio' : 'Agregar Servicio'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Línea 1: Nombre */}
          <div>
            <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Nombre *</label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              required
              data-testid="form-nombre"
            />
          </div>

          {/* Línea 2: Categoría y Dominio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Categoría *</label>
              <select
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-categoria"
              >
                <option value="Soporte">Soporte</option>
                <option value="Seguridad">Seguridad</option>
                <option value="Mantenimiento">Mantenimiento</option>
                <option value="Desarrollo">Desarrollo</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Dominio</label>
              <select
                value={formData.dominio_id}
                onChange={(e) => setFormData({ ...formData, dominio_id: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-dominio"
              >
                <option value="">Seleccionar dominio</option>
                {dominios.map(dominio => (
                  <option key={dominio.id} value={dominio.id}>{dominio.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Línea 3: Precio y Vencimiento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Precio *</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={formData.precio_base}
                  onChange={(e) => setFormData({ ...formData, precio_base: parseFloat(e.target.value) || 0 })}
                  className="flex-1 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                  required
                  data-testid="form-precio"
                />
                <button
                  type="button"
                  onClick={() => setIncludeIva(!includeIva)}
                  className={`px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs transition-colors whitespace-nowrap ${
                    includeIva
                      ? 'bg-[#00d68f] text-[#0d0f14] font-bold'
                      : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white hover:border-[#00d68f]'
                  }`}
                  data-testid="btn-iva"
                >
                  +IVA
                </button>
              </div>
              {includeIva && formData.precio_base > 0 && (
                <p className="text-[#00d68f] text-xs mt-1 font-['IBM_Plex_Mono']">
                  Con IVA (16%): ${calcularPrecioIva(formData.precio_base).toLocaleString()}
                </p>
              )}
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Vencimiento</label>
              <input
                type="date"
                value={formData.fecha_registro}
                onChange={(e) => setFormData({ ...formData, fecha_registro: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                min="2000-01-01"
                max="2099-12-31"
                data-testid="form-vencimiento"
              />
            </div>
          </div>

          {/* Línea 4: Periodo y Estado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Periodo *</label>
              <select
                value={formData.periodicidad}
                onChange={(e) => setFormData({ ...formData, periodicidad: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-periodicidad"
              >
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
                <option value="proyecto">Proyecto</option>
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Estado</label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-estado"
              >
                <option value="">Seleccionar estado</option>
                <option value="activo">Activo</option>
                <option value="por vencer">Por vencer</option>
                <option value="vencido">Vencido</option>
                <option value="pagado">Pagado</option>
              </select>
            </div>
          </div>

          {/* Línea 5: Descripción */}
          <div>
            <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              rows="3"
              data-testid="form-descripcion"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 bg-transparent border border-[#2a2e3d] text-white rounded-sm hover:border-[#4f9eff] transition-colors font-['IBM_Plex_Mono'] text-sm"
              data-testid="form-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm hover:bg-[#4f9eff]/90 transition-colors font-['IBM_Plex_Mono'] text-sm font-bold"
              data-testid="form-submit"
            >
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Servicios;